import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

interface UIState {
  isSidebarOpen: boolean
  isMobile: boolean
  theme: 'light' | 'dark' | 'system'
  language: string
  notifications: Notification[]
}

interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message: string
  timestamp: Date
  read: boolean
}

interface UIActions {
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
  setMobile: (isMobile: boolean) => void
  setTheme: (theme: 'light' | 'dark' | 'system') => void
  setLanguage: (language: string) => void
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void
  markNotificationRead: (id: string) => void
  clearNotifications: () => void
  removeNotification: (id: string) => void
}

export const useUIStore = create<UIState & UIActions>()(
  devtools(
    (set) => ({
      // State
      isSidebarOpen: false,
      isMobile: false,
      theme: 'system',
      language: 'ko',
      notifications: [],

      // Actions
      toggleSidebar: () => {
        set(
          (state) => ({ isSidebarOpen: !state.isSidebarOpen }),
          false,
          'ui/toggleSidebar'
        )
      },

      setSidebarOpen: (open) => {
        set({ isSidebarOpen: open }, false, 'ui/setSidebarOpen')
      },

      setMobile: (isMobile) => {
        set({ isMobile }, false, 'ui/setMobile')
      },

      setTheme: (theme) => {
        set({ theme }, false, 'ui/setTheme')
      },

      setLanguage: (language) => {
        set({ language }, false, 'ui/setLanguage')
      },

      addNotification: (notification) => {
        const newNotification: Notification = {
          ...notification,
          id: crypto.randomUUID(),
          timestamp: new Date(),
          read: false,
        }
        set(
          (state) => ({
            notifications: [newNotification, ...state.notifications],
          }),
          false,
          'ui/addNotification'
        )
      },

      markNotificationRead: (id) => {
        set(
          (state) => ({
            notifications: state.notifications.map((n) =>
              n.id === id ? { ...n, read: true } : n
            ),
          }),
          false,
          'ui/markNotificationRead'
        )
      },

      clearNotifications: () => {
        set({ notifications: [] }, false, 'ui/clearNotifications')
      },

      removeNotification: (id) => {
        set(
          (state) => ({
            notifications: state.notifications.filter((n) => n.id !== id),
          }),
          false,
          'ui/removeNotification'
        )
      },
    }),
    {
      name: 'ui-store',
    }
  )
)